import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import {
  type TransferResult,
  UserSelect,
  useStartTransfer,
  useTransferImpact,
  useWorkspaceMembers,
} from '@/entities/collab';
import { useWorkspaces } from '@/entities/workspace';
import { FormError } from '@/shared/forms';
import { Button, Combobox, Dialog, TextField } from '@/shared/ui';

import styles from './BoardAccess.module.css';

/**
 * Move this board to another workspace. The impact is shown first, then two confirmations: an
 * explicit yes and the exact board name. A side the initiator may approve for its workspace (its
 * owner, or a lead moderator delegated "approve transfers") is approved at once; the other side
 * gets a request and the board stays where it is until everybody required has approved.
 */
export function TransferTab({
  boardId,
  boardName,
  currentWorkspaceId,
}: {
  boardId: string;
  boardName: string;
  currentWorkspaceId: string;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const workspaces = useWorkspaces();
  const [destination, setDestination] = useState<string | null>(null);
  const [step, setStep] = useState<'choose' | 'confirm'>('choose');
  const [typed, setTyped] = useState('');
  const [nominee, setNominee] = useState<string | null>(null);
  const impact = useTransferImpact(boardId, step === 'confirm' ? destination : null);
  const members = useWorkspaceMembers(
    destination ?? '',
    step === 'confirm' && destination !== null,
  );
  const start = useStartTransfer(boardId);
  const [result, setResult] = useState<TransferResult | null>(null);
  const options = (workspaces.data ?? []).filter((w) => w.id !== currentWorkspaceId);

  if (result?.status === 'pending_approval') {
    return (
      <div className={styles.body}>
        <p className={styles.warn} role="status">
          {t('transfer.pendingApproval')}
        </p>
        <ul className={styles.sub}>
          {result.pending_source && <li>{t('transfer.waitingSource')}</li>}
          {result.pending_destination && <li>{t('transfer.waitingDestination')}</li>}
        </ul>
      </div>
    );
  }
  const canNominate = impact.isSuccess && !impact.data.destination_requires_approval;
  return (
    <div className={styles.body}>
      <p className={styles.sub}>{t('transfer.hint')}</p>
      <div className={styles.body}>
        <span>{t('transfer.destination')}</span>
        <Combobox
          label={t('transfer.destination')}
          value={destination}
          placeholder={t('transfer.choose')}
          searchPlaceholder={t('common.search')}
          emptyText={t('common.noResults')}
          options={options.map((w) => ({
            value: w.id,
            label: w.workspace_type === 'personal' ? t('transfer.personal') : w.name,
            group:
              w.workspace_type === 'personal'
                ? t('transfer.groupPersonal')
                : t('transfer.groupTeams'),
          }))}
          onChange={setDestination}
        />
      </div>
      <div className={styles.actions}>
        <Button
          variant="primary"
          disabled={destination === null}
          onClick={() => {
            setStep('confirm');
            setTyped('');
            setNominee(null);
          }}
        >
          {t('transfer.continue')}
        </Button>
      </div>
      {step === 'confirm' && destination && (
        <Dialog
          title={t('transfer.confirmTitle')}
          onClose={() => {
            setStep('choose');
          }}
        >
          <div className={styles.body}>
            {impact.isSuccess && (
              <>
                <p>
                  {t('transfer.summary', {
                    board: boardName,
                    workspace: impact.data.destination_name,
                  })}
                </p>
                <p className={styles.warn}>
                  {t('transfer.impact', {
                    people: impact.data.participants_losing_access.length,
                    rules: impact.data.access_rules_removed,
                    labels: impact.data.board_labels_removed,
                    invitations: impact.data.invitations_revoked + impact.data.links_revoked,
                  })}
                </p>
                <ul>
                  {impact.data.participants_losing_access.map((p) => (
                    <li key={p.user_id}>{p.display_name}</li>
                  ))}
                </ul>
                <p>
                  {!impact.data.source_requires_approval &&
                  !impact.data.destination_requires_approval
                    ? t('transfer.immediate')
                    : t('transfer.needsApproval')}
                </p>
                {impact.data.source_requires_approval && (
                  <p className={styles.sub}>{t('transfer.sourceApprovalNeeded')}</p>
                )}
                {impact.data.destination_requires_approval && (
                  <p className={styles.sub}>{t('transfer.destinationApprovalNeeded')}</p>
                )}
                {impact.data.owner_after_name && (
                  <p className={styles.sub}>
                    {t('transfer.ownerAfter', { name: impact.data.owner_after_name })}
                  </p>
                )}
              </>
            )}
            {canNominate && (
              <div className={styles.body}>
                <span>{t('transfer.nominateOwner')}</span>
                <UserSelect
                  label={t('transfer.nominateOwner')}
                  users={members.data ?? []}
                  value={nominee}
                  placeholder={t('transfer.nominateKeep')}
                  onChange={setNominee}
                />
              </div>
            )}
            <TextField
              label={t('transfer.typeName', { name: boardName })}
              value={typed}
              autoComplete="off"
              onChange={(event) => {
                setTyped(event.target.value);
              }}
            />
            <FormError error={start.error ?? impact.error} />
            <div className={styles.actions}>
              <Button
                onClick={() => {
                  setStep('choose');
                }}
              >
                {t('common.cancel')}
              </Button>
              <Button
                variant="primary"
                disabled={typed !== boardName || start.isPending || !impact.isSuccess}
                onClick={() => {
                  start.mutate(
                    {
                      destination_workspace_id: destination,
                      confirm: true,
                      confirm_name: typed,
                      ...(canNominate && nominee ? { new_owner_user_id: nominee } : {}),
                    },
                    {
                      onSuccess: (outcome) => {
                        setStep('choose');
                        if (outcome.status === 'completed') void navigate('/app');
                        else setResult(outcome);
                      },
                    },
                  );
                }}
              >
                {t('transfer.confirm')}
              </Button>
            </div>
          </div>
        </Dialog>
      )}
    </div>
  );
}
